import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Giveaways from "./pages/Giveaways";
import GiveawayDetail from "./pages/GiveawayDetail";
import Leaderboard from "./pages/Leaderboard";
import Dashboard from "./pages/Dashboard";
import DashboardOverview from "./pages/DashboardOverview";
import MyEntries from "./pages/MyEntries";
import SponsorLeads from "./pages/SponsorLeads";
import Referrals from "./pages/Referrals";
import Messages from "./pages/Messages";

// Growth Engine Pages
import CityPage from "./pages/CityPage";
import SponsorSignup from "./pages/SponsorSignup";
import ContractorSignup from "./pages/ContractorSignup";
import NominateNeighbor from "./pages/NominateNeighbor";
import AdminExpansion from "./pages/AdminExpansion";

// Admin Pages
import AdminGiveaways from "./pages/AdminGiveaways";
import FounderDashboard from "./pages/FounderDashboard";
import NotificationPreferences from "./pages/NotificationPreferences";
import AdminUsers from "./pages/AdminUsers";
import WinnerStoryGenerator from "./pages/WinnerStoryGenerator";
import AdminApprovals from "./pages/AdminApprovals";
import AdminOps from "./pages/AdminOps";
import AdminReferrals from "./pages/AdminReferrals";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import HomeownerQuestionnaire from "./pages/HomeownerQuestionnaire";

// New Pages
import ShareWin from "./pages/ShareWin";
import ProfilePage from "./pages/ProfilePage";
import SponsorCreateGiveaway from "./pages/SponsorCreateGiveaway";
import ContractorPortfolio from "./pages/ContractorPortfolio";
import PropertyEstimator from "./pages/PropertyEstimator";
import WinnerSpotlight from "./pages/WinnerSpotlight";
import SpotlightsGallery from "./pages/SpotlightsGallery";
import Envision from "./pages/Envision";
import Merch from "./pages/Merch";
import MerchSuccess from "./pages/MerchSuccess";
import ClaimMerch from "./pages/ClaimMerch";
import AdminMerchOrders from "./pages/AdminMerchOrders";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import AffiliateRedirect from "./pages/AffiliateRedirect";
import AffiliateLanding from "./pages/AffiliateLanding";
import SpinWheelPage from "./pages/SpinWheelPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SponsorROICalculator from "./pages/SponsorROICalculator";
import SponsorTerritory from "./pages/SponsorTerritory";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Reset scroll position on route change (respects #hash anchors)
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // CRITICAL: Handle OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/giveaways" element={<Giveaways />} />
      <Route path="/giveaways/:id" element={<GiveawayDetail />} />
      <Route path="/giveaways/:giveawayId/share" element={<ShareWin />} />
      <Route path="/giveaways/:giveawayId/spotlight" element={<WinnerSpotlight />} />
      <Route path="/spotlights" element={<SpotlightsGallery />} />
      <Route path="/spotlights/:spotlightId" element={<WinnerSpotlight />} />
      <Route path="/envision" element={<Envision />} />
      <Route path="/merch" element={<Merch />} />
      <Route path="/merch/success" element={<MerchSuccess />} />
      <Route path="/claim-merch" element={<ClaimMerch />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/sponsors" element={<Leaderboard />} />
      
      {/* Growth Engine Public Routes */}
      <Route path="/city/:slug" element={<CityPage />} />
      <Route path="/sponsor-signup" element={<SponsorSignup />} />
      <Route path="/roi-calculator" element={<SponsorROICalculator />} />
      <Route path="/contractor-signup" element={<ContractorSignup />} />
      <Route path="/nominate" element={<NominateNeighbor />} />
      
      {/* Affiliate Link Redirect */}
      <Route path="/r/:slug" element={<AffiliateRedirect />} />
      <Route path="/refer" element={<AffiliateLanding />} />
      <Route path="/affiliate" element={<AffiliateLanding />} />
      <Route path="/spin" element={<SpinWheelPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route
        path="/complete-entry"
        element={
          <ProtectedRoute>
            <HomeownerQuestionnaire />
          </ProtectedRoute>
        }
      />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverview />} />
        <Route path="entries" element={<MyEntries />} />
        <Route path="leads" element={<SponsorLeads />} />
        <Route path="referrals" element={<Referrals />} />
        <Route path="affiliate" element={<AffiliateDashboard />} />
        <Route path="messages" element={<Messages />} />
        <Route path="analytics" element={<DashboardOverview />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="create-giveaway" element={<SponsorCreateGiveaway />} />
        <Route path="contractor-portfolio" element={<ContractorPortfolio />} />
        <Route path="property-estimate" element={<PropertyEstimator />} />
        <Route path="territory" element={<SponsorTerritory />} />
        
        {/* Admin Routes */}
        <Route path="admin/approvals" element={<AdminApprovals />} />
        <Route path="admin/expansion" element={<AdminExpansion />} />
        <Route path="admin/giveaways" element={<AdminGiveaways />} />
        <Route path="admin/founder" element={<FounderDashboard />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/ops" element={<AdminOps />} />
        <Route path="admin/referrals" element={<AdminReferrals />} />
        <Route path="admin/merch-orders" element={<AdminMerchOrders />} />
        <Route path="admin/sponsors" element={<DashboardOverview />} />
        <Route path="admin/story-generator" element={<WinnerStoryGenerator />} />
        <Route path="notifications" element={<NotificationPreferences />} />
        <Route path="nominate" element={<NominateNeighbor />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
